import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Paper, Chip, Button, IconButton, Tooltip
} from "@mui/material";
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  FilterList as FilterIcon, FileDownload as ExportIcon
} from "@mui/icons-material";
import { fetchData, deleteData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import GuidelineFormDialog from "./GuidelineFormDialog";

const CATEGORY_COLORS = {
  Planning: { color: "#244C77", bg: "#EAF1F8" },
  General: { color: "#2196f3", bg: "#e3f2fd" }
};

const AdminProjectGuidelines = () => {
  const [guidelines, setGuidelines] = useState([]);
  const [stats, setStats] = useState({ total: 2, published: 2, unpublished: 0 });
  const [selectedGuideline, setSelectedGuideline] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadGuidelines = useCallback(async () => {
    try {
      const result = await fetchData("admin/project-guidelines/");
      if (result?.data) {
        setGuidelines(result.data.guidelines || result.data || []);
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load guidelines" });
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const result = await fetchData("admin/project-guidelines/stats");
      if (result?.data) setStats(result.data);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => { loadGuidelines(); loadStats(); }, [loadGuidelines, loadStats]);

  const handleDelete = async (id) => {
    try {
      await deleteData(`admin/project-guidelines/${id}`);
      setToast({ open: true, severity: "success", message: "Guideline deleted" });
      loadGuidelines();
      loadStats();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
    }
  };

  const handleFormSuccess = (message) => {
    setToast({ open: true, severity: "success", message });
    setFormOpen(false);
    setSelectedGuideline(null);
    loadGuidelines();
    loadStats();
  };

  const handleExport = () => {
    setToast({ open: true, severity: "info", message: "Export started..." });
  };

  const kpiCards = [
    { label: "Total", value: stats.total, color: "#244C77", bg: "#EAF1F8" },
    { label: "Published", value: stats.published, color: "#4caf50", bg: "#e8f5e9" },
    { label: "Un-Published", value: stats.unpublished, color: "#ff9800", bg: "#fff3e0" }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Project Guidelines</Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>Project Guidelines</Typography>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        {kpiCards.map((kpi, idx) => (
          <Grid item xs={4} key={idx}>
            <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: kpi.bg }}>
              <Typography variant="h4" fontWeight="bold" color={kpi.color}>{kpi.value}</Typography>
              <Typography variant="body2" color="text.secondary">{kpi.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Top Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 3 }}>
        <Button variant="outlined" startIcon={<FilterIcon />}
          sx={{ textTransform: "none" }}>Filters</Button>
        <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}
          sx={{ textTransform: "none" }}>Export</Button>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setSelectedGuideline(null); setFormOpen(true); }}
          sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", textTransform: "none" }}>
          New Guideline
        </Button>
      </Box>

      {/* Guideline Cards */}
      <Grid container spacing={3}>
        {guidelines.map((g) => {
          const catStyle = CATEGORY_COLORS[g.category] || CATEGORY_COLORS.General;
          return (
            <Grid item xs={12} md={6} key={g.id}>
              <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #e0e0e0", position: "relative" }}>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" mb={1}>
                      {g.title || "Untitled"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                      {g.category && (
                        <Chip label={g.category} size="small" sx={{
                          backgroundColor: catStyle.bg, color: catStyle.color, fontWeight: 600
                        }} />
                      )}
                      {g.grade && (
                        <Chip label={g.grade} size="small" sx={{
                          backgroundColor: "#f5f5f5", color: "#757575"
                        }} />
                      )}
                      <Chip label={g.status === "published" ? "Published" : "Unpublished"} size="small"
                        sx={{
                          backgroundColor: g.status === "published" ? "#e8f5e9" : "#fff3e0",
                          color: g.status === "published" ? "#4caf50" : "#ff9800",
                          fontWeight: 600
                        }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setSelectedGuideline(g); setFormOpen(true); }}>
                        <EditIcon fontSize="small" sx={{ color: "#2196f3" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(g.id)}>
                        <DeleteIcon fontSize="small" sx={{ color: "#f44336" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Description */}
                <Typography variant="body2" color="text.secondary" mb={2} sx={{
                  display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                  overflow: "hidden", textOverflow: "ellipsis"
                }}>
                  {g.description || "No description available."}
                </Typography>

                {/* Footer */}
                <Typography variant="caption" color="text.secondary">
                  Published: {g.publishedDate || g.createdAt?.slice(0, 10) || "-"}
                </Typography>
              </Paper>
            </Grid>
          );
        })}

        {guidelines.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                No guidelines found. Click "New Guideline" to create one.
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <GuidelineFormDialog open={formOpen} onClose={() => { setFormOpen(false); setSelectedGuideline(null); }}
        guideline={selectedGuideline} onSuccess={handleFormSuccess} />

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminProjectGuidelines;
